import React from "react"

import { ChevronDown, ChevronUp, Trash2 } from "lucide-react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useTrialBlock } from "@/contexts/TrialBlockContext"

import EmptyBlocksState from "./EmptyBlocksState"

const TrialBlocksTable = () => {
  const { filteredBlocks, handleOpenReview, handleShowDetails, sortConfig, requestSort } =
    useTrialBlock()

  const getSortDirection = (name: string) => {
    if (!sortConfig || sortConfig.key !== name) {
      return null
    }
    return sortConfig.direction === "ascending" ? (
      <ChevronUp size={16} />
    ) : (
      <ChevronDown size={16} />
    )
  }

  const handleDeleteBlock = (blockId: string) => {
    // In a real implementation, we would call a deletion API
    // For now, just show a toast confirmation
    toast.success("Trial Block Deleted", {
      description: `Trial Block #${blockId} has been deleted.`,
    })
  }

  // Display the empty state if there are no matching blocks
  if (filteredBlocks.length === 0) {
    return <EmptyBlocksState />
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="cursor-pointer" onClick={() => requestSort("driverName")}>
              <div className="flex items-center">Driver Name {getSortDirection("driverName")}</div>
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => requestSort("location")}>
              <div className="flex items-center">Location {getSortDirection("location")}</div>
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => requestSort("date")}>
              <div className="flex items-center">Date {getSortDirection("date")}</div>
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => requestSort("reviewStatus")}>
              <div className="flex items-center">Status {getSortDirection("reviewStatus")}</div>
            </TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredBlocks.map((block) => (
            <TableRow key={block.id}>
              <TableCell>{block.driverName}</TableCell>
              <TableCell>{block.location}</TableCell>
              <TableCell>{block.date}</TableCell>
              <TableCell>
                <span
                  className={
                    block.reviewStatus === "pending"
                      ? "bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs"
                      : "bg-green-100 text-green-800 px-2 py-1 rounded text-xs"
                  }
                >
                  {block.reviewStatus === "pending" ? "Pending Review" : "Reviewed"}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      block.reviewStatus === "pending"
                        ? handleOpenReview(block.id)
                        : handleShowDetails(block.id)
                    }
                  >
                    {block.reviewStatus === "pending" ? "Review" : "View"}
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Trial Block</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this trial block for {block.driverName}?
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteBlock(block.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default TrialBlocksTable
